import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar } from '../../components_catalyst/avatar';
import { AddEditDeleteBar1 } from './AddEditDeleteBar1';
import { AddEditComment1 } from './AddEditComment1';
import { updateObjective } from '../../store/map3PendingSlice';
import { AppDispatch, RootState } from '../../store/store';
import { VideoEmbed } from './VideoEmbed1';
import { formatDateForComments } from '../../utils/formatDate';
import { useCurrentMap } from '../../hooks/useCurrentMap';
// import { selectUserRoleForMap, selectUserProfile } from '../../store/selectors';
import { selectUserProfile } from '../../store/selectors';
import { useRoleWithOverride } from '../../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '@mm-shared/authz';
import { getInitials, getAvatarColor } from '../../utils/avatarUtils';
import { formatAuthorWithVia } from '../../utils/provenance';

interface CommentBubble1Props {
  commentUuid: string;
  showTopBorder?: boolean;
  parentObjectiveUuid: string;
}

export function CommentBubble1({
  commentUuid,
  showTopBorder = true,
  parentObjectiveUuid,
}: CommentBubble1Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [actionBarActive, setActionBarActive] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj) => obj.uuid === parentObjectiveUuid),
  );
  const comment = objective?.comments.find((c) => c.uuid === commentUuid);
  const userProfile = useSelector(selectUserProfile);

  // Check user's role for comment edit/delete permissions
  const currentMap = useCurrentMap();
  // const userRole = useSelector((state: RootState) =>
  //   currentMap ? selectUserRoleForMap(state, currentMap.uuid) : null,
  // );
  const userRole = useRoleWithOverride(currentMap?.uuid || '');
  const { canComment } = getPermissionsForRole(userRole);

  // Users can only edit/delete their own comments
  const isCommentAuthor = comment?.createdBy === userProfile?.userName;
  const canEditComment = canComment && isCommentAuthor;

  // Generate consistent avatar details from real user data
  const avatarDetails = useMemo(() => {
    const userName = comment?.createdBy || 'Unknown User';
    return {
      initials: getInitials(userName),
      colorClass: getAvatarColor(userName),
    };
  }, [comment?.createdBy]);

  if (!comment) {
    return null; // or some error state UI
  }

  function handleDelete(commentUuid: string) {
    if (!objective) return;
    if (!canEditComment) {
      console.warn('Comment delete denied: user does not have edit permissions');
      return;
    }

    dispatch(
      updateObjective({
        uuid: parentObjectiveUuid,
        updates: {
          comments: objective.comments.filter((c) => c.uuid !== commentUuid),
        },
      }),
    );
  }

  if (isEditing) {
    return (
      <AddEditComment1
        objectiveUuid={parentObjectiveUuid}
        forIntervalEnding={comment.forIntervalEnding}
        commentToEdit={comment}
        onSave={() => setIsEditing(false)}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  return (
    <article
      className="flex space-x-4 text-sm text-gray-500 relative rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onFocus={() => setShowActions(true)}
      onBlur={(e) => {
        // Only hide actions if focus is moving outside the comment
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setShowActions(false);
          setActionBarActive(false);
        }
      }}
      tabIndex={0}
      role="listitem"
      aria-label={`Comment by ${comment.createdBy} from ${formatDateForComments(comment.createdDate)}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !isEditing && !actionBarActive) {
          e.preventDefault();
          e.stopPropagation();
          setActionBarActive(true);
          setShowActions(true);
        } else if (e.key === 'Escape' && actionBarActive) {
          e.preventDefault();
          e.stopPropagation();
          setActionBarActive(false);
          // Focus will return to the comment via onActionBarEscape
        }
      }}
    >
      <div className="flex-none py-10">
        <Avatar
          initials={avatarDetails.initials}
          className={`size-10 ${avatarDetails.colorClass} text-white`}
          alt={`${comment.createdBy || 'Unknown User'} avatar`}
        />
      </div>
      <div className={`flex-1 py-10 ${!showTopBorder ? '' : 'border-t border-gray-200'}`}>
        <div className="flex justify-between">
          <div>
            <h3 className="font-medium text-gray-900">
              {formatAuthorWithVia(comment.createdBy, comment.via)}
            </h3>
            <p>
              <time dateTime={comment.createdDate}>
                {formatDateForComments(comment.createdDate)}
              </time>
            </p>
          </div>
          <div className="relative">
            <AddEditDeleteBar1
              isVisible={showActions}
              showAdd={false}
              showEdit={canEditComment}
              showDelete={canEditComment}
              showFlag={false}
              onEdit={() => {
                if (!canEditComment) {
                  console.warn('Comment edit denied: user does not have edit permissions');
                  return;
                }
                setIsEditing(true);
              }}
              onDelete={() => handleDelete(comment.uuid)}
              actionBarActive={actionBarActive}
              onActionBarEscape={() => {
                setActionBarActive(false);
                // Return focus to the comment
                const commentElement = document.querySelector(
                  `article[aria-label*="${comment.createdBy}"]`,
                ) as HTMLElement;
                commentElement?.focus();
              }}
            />
          </div>
        </div>
        {comment.videoUrl && (
          <div className="mt-4">
            <VideoEmbed url={comment.videoUrl} />
          </div>
        )}
        <div className="mt-4 text-sm/6 text-gray-500">{comment.text}</div>
      </div>
    </article>
  );
}
